import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { MagnifyingGlassIcon, SparklesIcon, XMarkIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";
import { Investigation } from "../../db/schema";
import { api, InvestigationListItem } from "./api";

type FormValues = { name: string; case_number: string; description: string; focus: string };

interface Props {
  open: boolean;
  onClose: () => void;
  // Create mode
  onCreated?: (created: InvestigationListItem) => void | Promise<void>;
  // Edit mode: pass the investigation being edited and a save handler.
  investigation?: Pick<Investigation, "name" | "case_number" | "description" | "focus"> | null;
  onSaved?: (values: FormValues) => void | Promise<void>;
  // Edit mode: optional "save and re-run analysis" action.
  onSaveAndReanalyze?: (values: FormValues) => void | Promise<void>;
}

const FOCUS_EXAMPLES = ["where the gun went", "anyone contacting the witness", "money moving out of state"];

const field = "w-full rounded-lg border border-gray-300 dark:border-base-300 bg-base-100 px-3 py-2 text-sm text-base-content placeholder:text-base-content/40 outline-none focus:border-blue-500 transition-colors";
const label = "block text-[11px] font-semibold uppercase tracking-wider text-base-content/60 mb-1.5";

const NewInvestigationModal: React.FC<Props> = ({ open, onClose, onCreated, investigation, onSaved, onSaveAndReanalyze }) => {
  const editing = Boolean(investigation);
  const [form, setForm] = useState<FormValues>({ name: "", case_number: "", description: "", focus: "" });
  const [saving, setSaving] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const focusChanged = editing && form.focus.trim() !== (investigation?.focus || "").trim();

  useEffect(() => {
    if (!open) return;
    setForm({
      name: investigation?.name || "",
      case_number: investigation?.case_number || "",
      description: investigation?.description || "",
      focus: investigation?.focus || "",
    });
    setTimeout(() => nameRef.current?.focus(), 50);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !saving && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const create = async (reanalyze = false) => {
    if (!form.name.trim()) return toast.error("Give the investigation a name");
    setSaving(true);
    try {
      if (editing) {
        if (reanalyze && onSaveAndReanalyze) await onSaveAndReanalyze(form);
        else await onSaved?.(form);
      } else {
        const created = await api.create({ name: form.name, case_number: form.case_number, description: form.description, focus: form.focus });
        await onCreated?.(created);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.error || (editing ? "Failed to save" : "Failed to create investigation"));
    } finally {
      setSaving(false);
    }
  };

  const addFocus = (text: string) => setForm((f) => ({ ...f, focus: f.focus ? `${f.focus.replace(/[.;\s]+$/, "")}; ${text}` : text }));

  return (
    <dialog open className="modal modal-open">
      <form
        method="dialog"
        className="modal-box max-w-xl p-0 overflow-hidden rounded-2xl"
        onSubmit={(e) => {
          e.preventDefault();
          create();
        }}
      >
        <div className="flex items-start gap-3 px-6 pt-6 pb-4 border-b border-base-300">
          <div className="size-10 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center">
            <MagnifyingGlassIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-base-content leading-tight">{editing ? "Edit investigation" : "New investigation"}</h3>
            <p className="text-sm text-base-content/60 mt-0.5">
              {editing ? "Update the details or refine what the AI should look for." : "Name it, then tell the AI what matters. You'll add calls and files next."}
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
            <XMarkIcon className="size-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-[1fr_9rem] gap-3">
            <div>
              <label className={label} htmlFor="inv-name">Name</label>
              <input id="inv-name" ref={nameRef} className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Elm St shooting — Lopez" maxLength={200} />
            </div>
            <div>
              <label className={label} htmlFor="inv-case">Case #</label>
              <input id="inv-case" className={cn(field, "tabular-nums")} value={form.case_number} onChange={(e) => setForm({ ...form, case_number: e.target.value })} placeholder="26-004512" maxLength={100} />
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <label className={label} htmlFor="inv-bg">Background <span className="normal-case tracking-normal font-normal text-base-content/40">· optional</span></label>
              <span className="text-[11px] tabular-nums text-base-content/40">{form.description.length}/4000</span>
            </div>
            <textarea id="inv-bg" rows={3} className={cn(field, "resize-none leading-5")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What happened, who's involved, what you already know." maxLength={4000} />
          </div>

          <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-900/20 p-4">
            <div className="flex items-baseline justify-between">
              <label className={cn(label, "text-blue-600 dark:text-blue-300 flex items-center gap-1.5")} htmlFor="inv-focus">
                <SparklesIcon className="size-3.5" /> What should the AI focus on? <span className="normal-case tracking-normal font-normal text-blue-600/60 dark:text-blue-300/60">· optional</span>
              </label>
              <span className="text-[11px] tabular-nums text-base-content/40">{form.focus.length}/2000</span>
            </div>
            <textarea id="inv-focus" rows={2} className={cn(field, "resize-none leading-5 bg-base-100")} value={form.focus} onChange={(e) => setForm({ ...form, focus: e.target.value })} placeholder="Where the gun went; anyone contacting the witness on 5th St; references to 'the package'." maxLength={2000} />
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-base-content/50">Try:</span>
              {FOCUS_EXAMPLES.map((t) => (
                <button key={t} type="button" onClick={() => addFocus(t)} className="text-[11px] px-2 py-0.5 rounded-full border border-blue-200 dark:border-blue-800 bg-base-100 text-base-content/70 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-300 transition-colors">
                  {t}
                </button>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-base-content/50">
              {editing
                ? "Steers what gets flagged and how calls are ranked. Changes apply to new uploads; use \"Save & re-run\" to apply them to calls already analyzed."
                : "Steers what gets flagged and how calls are ranked. You can change it later and re-run."}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 bg-base-200/60 border-t border-base-300">
          <span className="text-[11px] text-base-content/40 hidden sm:inline">{editing ? "Enter to save" : "Enter to create"} · Esc to cancel</span>
          <div className="flex items-center gap-2 ml-auto">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            {editing && onSaveAndReanalyze && (
              <Button type="button" variant={focusChanged ? "primary" : "secondary"} size="sm" onClick={() => create(true)} disabled={!form.name.trim() || saving} title="Save, then re-run AI analysis on every call with the new focus">
                <SparklesIcon className="size-4" /> Save & re-run
              </Button>
            )}
            <Button type="submit" variant={editing && focusChanged ? "secondary" : "primary"} size="sm" loading={saving} disabled={!form.name.trim() || saving}>
              {editing ? "Save" : "Create Investigation"}
            </Button>
          </div>
        </div>
      </form>
      <div className="modal-backdrop bg-black/40" onClick={() => !saving && onClose()} />
    </dialog>
  );
};

export default NewInvestigationModal;
